/**
 * TrelloButton component for sending leads to Trello
 * Handles authentication and card creation with status indication
 */

import React, { useCallback } from 'react';
import { Lead } from '../../types/Lead';
import { TRELLO_CONFIG } from '../../config/trello';
import { useTrelloIntegration } from '../../hooks/useTrelloIntegration';

interface TrelloButtonProps {
  lead: Lead;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  listId?: string;
}

export const TrelloButton: React.FC<TrelloButtonProps> = ({
  lead,
  size = 'medium',
  disabled = false,
  listId = TRELLO_CONFIG.LIST_ID
}) => {
  const { isLoading, isSent, error, sendToTrello } = useTrelloIntegration(lead.id);

  // Size configurations
  const sizeConfig = {
    small: { padding: '6px 12px', fontSize: '12px', iconSize: 14 },
    medium: { padding: '8px 16px', fontSize: '14px', iconSize: 16 },
    large: { padding: '10px 20px', fontSize: '16px', iconSize: 18 }
  };

  const config = sizeConfig[size];

  const handleTrelloAction = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (disabled || isLoading || isSent) return;

    await sendToTrello(lead, listId);
  }, [disabled, isLoading, isSent, sendToTrello, lead, listId]);

  // Button styling based on state
  const getButtonStyle = () => {
    const baseStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: config.padding,
      border: 'none',
      borderRadius: '6px',
      fontSize: config.fontSize,
      fontWeight: '500',
      cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease',
      opacity: disabled ? 0.5 : 1,
      textAlign: 'center' as const
    };

    if (isSent) {
      return { ...baseStyle, backgroundColor: '#10b981', color: 'white', cursor: 'default' };
    }
    if (error) {
      return { ...baseStyle, backgroundColor: '#ef4444', color: 'white' };
    }
    return { ...baseStyle, backgroundColor: isLoading ? '#6b7280' : '#0ea5e9', color: 'white' };
  };

  const getButtonText = () => {
    if (isSent) return 'Sent to Trello';
    if (isLoading) return 'Sending...';
    if (error) return 'Try Again';
    return 'Move to Trello';
  };

  const getButtonIcon = () => {
    if (isSent) {
      return (
        <svg width={config.iconSize} height={config.iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
        </svg>
      );
    }

    if (isLoading) {
      return (
        <div
          style={{
            width: config.iconSize,
            height: config.iconSize,
            border: '2px solid rgba(255,255,255,0.3)',
            borderTop: '2px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
        />
      );
    }

    return (
      <svg width={config.iconSize} height={config.iconSize} viewBox="0 0 24 24" fill="currentColor">
        <path d="M21 0H3C1.343 0 0 1.343 0 3v18c0 1.657 1.343 3 3 3h18c1.657 0 3-1.343 3-3V3c0-1.657-1.343-3-3-3zM10.5 17.5c0 .828-.672 1.5-1.5 1.5H6c-.828 0-1.5-.672-1.5-1.5v-11C4.5 5.672 5.172 5 6 5h3c.828 0 1.5.672 1.5 1.5v11zm9-4c0 .828-.672 1.5-1.5 1.5h-3c-.828 0-1.5-.672-1.5-1.5v-7C13.5 5.672 14.172 5 15 5h3c.828 0 1.5.672 1.5 1.5v7z"/>
      </svg>
    );
  };

  const buttonTitle = isSent 
    ? 'Lead has been sent to Trello' 
    : error 
      ? `Error: ${error}`
      : 'Send lead to Trello board';

  return (
    <button
      onClick={handleTrelloAction}
      disabled={disabled || isLoading || isSent}
      style={getButtonStyle()}
      title={buttonTitle}
      aria-label={getButtonText()}
    >
      {getButtonIcon()}
      <span>{getButtonText()}</span>
    </button>
  );
};